import { z } from 'zod';
export const mailSettingsInput = z.object({
    host: z.string().trim().min(3, 'Servidor inválido').max(200),
    port: z.coerce.number().int().min(1).max(65535).default(587),
    secure: z.boolean().default(false),
    username: z.string().trim().max(200).nullable().optional(),
    /** Vacío = conservar la guardada. */
    password: z.string().max(300).optional(),
    fromName: z.string().trim().max(120).nullable().optional(),
    fromEmail: z.email('Correo inválido'),
    replyTo: z
        .email('Correo inválido')
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
    bcc: z
        .email('Correo inválido')
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
});
/**
 * Una plantilla por tipo de comprobante, como en el sistema anterior. `document` es la de la factura
 * electrónica (conserva el nombre para no perder las plantillas ya guardadas). Cada tipo usa la suya guardada o
 * su texto predeterminado.
 */
export const MAIL_TEMPLATE_KINDS = [
    'document',
    'ticket',
    'credit_note',
    'debit_note',
    'purchase',
    'quote',
    'reminder',
];
export const MAIL_TEMPLATE_LABELS = {
    document: 'Factura',
    ticket: 'Tiquete',
    credit_note: 'Nota de crédito',
    debit_note: 'Nota de débito',
    purchase: 'Factura de compra',
    quote: 'Cotización',
    reminder: 'Recordatorio',
};
/** Plantilla que corresponde al tipo de comprobante cuando se envía el comprobante en sí. */
export function mailTemplateKindFor(documentKind) {
    return ({
        TE: 'ticket',
        NC: 'credit_note',
        ND: 'debit_note',
        FEC: 'purchase',
    }[documentKind] ?? 'document');
}
export const mailTemplateInput = z.object({
    subject: z.string().trim().min(3).max(200),
    body: z.string().trim().min(10).max(5000),
});
/**
 * Texto de `{aceptacion}`: solo afirma la aceptación si Hacienda ya respondió que sí. Con otro estado queda vacío
 * (nunca se le dice al cliente que algo está aceptado cuando no lo está).
 */
export function acceptanceText(haciendaStatus, documentKind) {
    if (haciendaStatus === 'accepted') {
        return `, ${documentKind === 'TE' ? 'aceptado' : 'aceptada'} por el Ministerio de Hacienda`;
    }
    if (haciendaStatus === 'internal')
        return ' (comprobante interno, sin validez fiscal)';
    return '';
}
/** Quita la frase de aceptación escrita a mano en una plantilla guardada cuando el comprobante no está aceptado. */
export function stripAcceptanceClaim(text) {
    return text.replace(/,?\s*(?:y\s+)?aceptad[oa]s?\s+por\s+el\s+Ministerio\s+de\s+Hacienda/gi, '');
}
/** Variables disponibles en las plantillas. */
export const MAIL_VARIABLES = [
    '{cliente}',
    '{compania}',
    '{documento}',
    '{numero}',
    '{total}',
    '{saldo}',
    '{vence}',
    '{clave}',
    '{aceptacion}',
];
export const DEFAULT_TEMPLATES = {
    document: {
        subject: '{documento} {numero} de {compania}',
        body: 'Hola {cliente}:\n\nAdjuntamos la {documento} {numero} por un total de {total}{aceptacion}.\n\nEncontrás el PDF y los archivos XML adjuntos.\n\nGracias por tu preferencia,\n{compania}',
    },
    ticket: {
        subject: '{documento} {numero} de {compania}',
        body: 'Hola {cliente}:\n\nAdjuntamos el {documento} {numero} por un total de {total}{aceptacion}.\n\nEncontrás el PDF y los archivos XML adjuntos.\n\nGracias por tu compra,\n{compania}',
    },
    credit_note: {
        subject: '{documento} {numero} de {compania}',
        body: 'Hola {cliente}:\n\nAdjuntamos la {documento} {numero} por {total}{aceptacion}, que ajusta un comprobante emitido anteriormente.\n\nEncontrás el PDF y los archivos XML adjuntos.\n\nSaludos,\n{compania}',
    },
    debit_note: {
        subject: '{documento} {numero} de {compania}',
        body: 'Hola {cliente}:\n\nAdjuntamos la {documento} {numero} por {total}{aceptacion}, que ajusta un comprobante emitido anteriormente.\n\nEncontrás el PDF y los archivos XML adjuntos.\n\nSaludos,\n{compania}',
    },
    purchase: {
        subject: '{documento} {numero} de {compania}',
        body: 'Hola {cliente}:\n\nAdjuntamos la {documento} {numero} por {total}{aceptacion}, emitida por {compania} por la compra de bienes o servicios.\n\nEncontrás el PDF y los archivos XML adjuntos.\n\nSaludos,\n{compania}',
    },
    quote: {
        subject: 'Cotización {numero} de {compania}',
        body: 'Hola {cliente}:\n\nTe compartimos la cotización {numero} por un total de {total}, válida hasta el {vence}.\n\nQuedamos atentos a cualquier consulta.\n\nSaludos,\n{compania}',
    },
    reminder: {
        subject: 'Recordatorio de pago · {documento} {numero}',
        body: 'Hola {cliente}:\n\nTe recordamos que la {documento} {numero} por {total} vence el {vence}.\n\nSi ya realizaste el pago, por favor ignorá este mensaje.\n\nSaludos,\n{compania}',
    },
};
export const sendDocumentEmailInput = z.object({
    to: z.array(z.email('Correo inválido')).min(1, 'Agregá al menos un destinatario').max(10),
    kind: z.enum(['document', 'reminder']).default('document'),
    /** Asunto y mensaje editados; si faltan se usa la plantilla del tipo de comprobante. */
    subject: z.string().trim().min(1).max(200).optional(),
    body: z.string().trim().min(1).max(5000).optional(),
    /** Adjuntos a incluir (por defecto todos los disponibles). */
    attachments: z
        .object({
        pdf: z.boolean().default(true),
        xml: z.boolean().default(true),
        response: z.boolean().default(true),
    })
        .optional(),
});
//# sourceMappingURL=mail.js.map